import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import useEnrolPageProps from "@hooks/useEnrolPageProps";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import { dispatchGAEvents } from "@/utils/events/dispatchGAEvents";
import useAppStateSync from "@/hooks/useAppStateSync";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { useAtom, useSetAtom } from "jotai";

import {
    Box,
    Divider,
    Flex,
    Image,
    SimpleGrid,
    Wrap,
    WrapItem
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";
import { CheckoutInterface } from "@ts/interfaces/Checkout/CheckoutInterface";
import { GradeInterface } from "@ts/interfaces/Checkout/GradeInterface";
import { ProductFamilyInterface } from "@ts/interfaces/Checkout/ProductFamilyInterface";

import {
    checkoutAtom,
    checkoutStudentFlowAtom,
    isNewsletterSubEnabledAtom,
    isProvidedQueryParamsAtom,
    pageQueryParamsAtom,
    parentIdAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom.ts";

import { dispatchHSEvents } from "@utils/events/dispatchHSEevents";
import {
    getPlansForTargetGrade,
    getSubjectsByGrade,
    getTrackingId,
    removeNullProperties,
    sortByProperty
} from "@utils/utils";

import ContentLayout from "@layouts/ContentLayout";

import { Card } from "@components/Card";
import { SelectablePill } from "@components/SelectablePill";
import { Skeleton } from "@components/Skeleton";
import { Text } from "@components/Text";

const AgeGroupContainer = () => {
    const {
        state: { checkout: checkoutAppState }
    } = useAppStateSync();

    const { hubspotEventCheckout, parentUser, subjectsByGrade } =
        useCheckoutPageProps();
    const { hubspotEventEnrol } = useEnrolPageProps();

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [checkout, setCheckout] = useAtom(checkoutAtom);

    const checkoutContext = useContext(CheckoutContext);

    const {
        userSelectedCountry,
        userSelectedLang,
        userSelectedState,
        productFamilies,
        isProductFamiliesLoading
    } = checkoutContext;

    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const [isNewsletterSubEnabled] = useAtom(isNewsletterSubEnabledAtom);
    const setIsProvidedQueryParams = useSetAtom(isProvidedQueryParamsAtom);
    const [checkoutStudentFlow] = useAtom(checkoutStudentFlowAtom);

    const [parentId] = useAtom(parentIdAtom);

    const [searchParams, setSearchParams] = useSearchParams();
    const origin = searchParams.get("origin");

    const [pathNavigateTo, setPathNavigateTo] = useState(
        userSelectedCountry === "VN"
            ? CheckoutPageRoutesEnum.PLAN_SELECT_PAGE
            : CheckoutPageRoutesEnum.GRADE_CURRICULUM_PAGE
    );
    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutationState, checkoutStateMutate } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(pathNavigateTo));
        });

    const isProductFamilySelected = !!checkout.productFamily;
    const isParentUserLoggedIn = !!parentUser;

    const subjects = getSubjectsByGrade(
        subjectsByGrade,
        checkout.grade?.id as number
    );

    const onSelectProductFamily = (
        grade: GradeInterface,
        productFamily: ProductFamilyInterface
    ) => {
        let defaultAddon: null | AddonInterface = null;
        const defaultPlan =
            getPlansForTargetGrade(productFamily.plans, grade.name).find(
                p => p.isPreSelectedPlan
            ) ?? null;

        if (defaultPlan) {
            const availableAddons = defaultPlan.addons;
            if (availableAddons) {
                defaultAddon =
                    availableAddons.find(a => !a.isHighlighted) ?? null;
            }
        }
        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                checkout_status: "ABANDON",
                email: checkout.user.email,
                checkout_token: userCheckoutStateToken,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang,
                checkout_grade: grade.name,
                checkout_indicative_plan: defaultPlan
                    ? defaultPlan.id
                    : "Euka-Early-Age-Clever-Kids"
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );
        setCheckout((prev: CheckoutInterface | Promise<CheckoutInterface>) => ({
            ...prev,
            productFamily,
            grade,
            plan: defaultPlan,
            addon: defaultAddon,
            charge: null,
            origin
        }));
    };

    const onSaveData = () => {
        let pathNavigateTo = CheckoutPageRoutesEnum.ADDON_SELECT_PAGE;
        const isUserInfoPageCompleted =
            !!checkout.user.email && !!checkout.user.firstName;

        /**
         * If the selected product family has one plan, that plan needs to be selected by default
         * and skip the plan selection page being displayed.
         */
        if (
            checkout.productFamily &&
            checkout.productFamily.plans.length === 1
        ) {
            const [selectedPlan] = checkout.productFamily.plans;
            /**
             * Some plans may not have associated addons, and some plans may have addons
             * with a price of 0 (which are included by default). Therefore, users won't be selecting these addons, so the
             * addon selection page is skipped in such scenario.
             */
            if (
                selectedPlan.addons &&
                // logical AND is short-circuiting
                selectedPlan.addons.every(a => !a.isIncluded)
            ) {
                setPathNavigateTo(pathNavigateTo);
            } else {
                pathNavigateTo = CheckoutPageRoutesEnum.PAYMENT_PAGE;
                setPathNavigateTo(pathNavigateTo);
            }

            setCheckout(prev => ({
                ...prev,
                plan: selectedPlan,
                nextAttemptPage: CheckoutPagesEnum.PLAN_SELECT_PAGE
            }));
        } else {
            if (isParentUserLoggedIn && subjects && !subjects.length) {
                pathNavigateTo = CheckoutPageRoutesEnum.PLAN_SELECT_PAGE;
                setPathNavigateTo(pathNavigateTo);
            }
            if (isUserInfoPageCompleted && !isParentUserLoggedIn) {
                pathNavigateTo = CheckoutPageRoutesEnum.PLAN_SELECT_PAGE;
                setPathNavigateTo(pathNavigateTo);
            }
        }

        if (isParentUserLoggedIn && subjects && !subjects.length) {
            pathNavigateTo = CheckoutPageRoutesEnum.PLAN_SELECT_PAGE;
            setPathNavigateTo(pathNavigateTo);
        }

        dispatchHSEvents(
            "trackPageView",
            pathNavigateTo,
            undefined,
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        dispatchGAEvents({
            event: "select_product",
            productData: {
                grade_selected: `${checkout.productFamily!.name} - ${
                    checkout.grade!.name
                }`
            },
            parentId
        });

        checkoutStateMutate();
    };

    /**
     * "useEffect" hook to set the query parameters dynamically based on the selected product.
     */
    useEffect(() => {
        if (checkoutStateMutationState.isSuccess) {
            // Update the related query parameters with the selected grade and family.
            onSetupQueryParams({
                ...pageQueryParams,
                selectedCountry: userSelectedCountry,
                selectedLang: userSelectedLang,
                selectedState: userSelectedState,
                addon: checkout.addon ? checkout.addon.id : null,
                plan: checkout.plan ? checkout.plan.id : null,
                family: checkout.productFamily!.id,
                grade: checkout.grade!.name
            });
        }
    }, [checkout, checkoutStateMutationState]);

    const onGoBack = () =>
        navigate(
            routeWithQueryParams(CheckoutPageRoutesEnum.STATE_SELECT_PAGE)
        );

    useEffect(() => {
        if (
            pageQueryParams.origin === "ecommerce" &&
            isParentUserLoggedIn &&
            !checkoutStudentFlow
        ) {
            navigate(
                routeWithQueryParams(
                    CheckoutPageRoutesEnum.STUDENT_SELECTION_PAGE
                )
            );
        }
    }, []);

    useEffect(() => {
        if (checkoutAppState?.checkoutPurchasingAddon) {
            // TODO: Improve hardcorded check for contries don't need state selections
            navigate(
                userSelectedCountry === "VN"
                    ? CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
                    : CheckoutPageRoutesEnum.STATE_SELECT_PAGE
            );
        }
        if (checkoutAppState?.checkoutSubjectsFee) {
            navigate(CheckoutPageRoutesEnum.PAYMENT_PAGE);
        }
    }, []);

    useEffect(() => {
        // Set all the query parameters in the URL which were updated in "pageQueryParams" atom at this point.
        setSearchParams(removeNullProperties(pageQueryParams));
        setIsProvidedQueryParams(false);
    }, [pageQueryParams]);

    const selectedName = checkout.selectedStudent
        ? checkout.selectedStudent.first_name
        : null;
    const createdStudentName = checkout.student
        ? checkout.student.firstName
        : null;
    const studentName = selectedName || createdStudentName;

    return (
        <ContentLayout
            title={
                studentName
                    ? t(
                          "checkout.selectAgeGroupPage.titleWithStudentName",
                          "What grade do you want to enrol your {{studentName}} in?",
                          { studentName }
                      )
                    : t(
                          "checkout.selectAgeGroupPage.title",
                          "What grade do you want to enrol your child in?"
                      )
            }
            description={t(
                "checkout.selectAgeGroupPage.description",
                "If you are unsure of which grade to select, please select your child's current grade."
            )}
            headerBackButton
            footerActionsProps={{
                isNextBtnLoading: checkoutStateMutationState.isPending,
                canGoNext: isProductFamilySelected,
                btnClassHierarchy: {
                    next: [ClassesEnum.AgeGroupContainer, ClassesEnum.Button]
                },
                btnActions: {
                    next: "goNextFromProductFamilySelectPage"
                },
                // TODO: Improve hardcorded check for contries don't need state selections
                hasBackButton: userSelectedCountry !== "VN",
                onGoNext: onSaveData,
                onGoBack
            }}
            step={2}
            containerWidth={["95%", null, null, null, null]}
            className={ClassesEnum.AgeGroupContainer}
            // noticeElement={
            //     !isParentUserLoggedIn && (
            //         <Box
            //             w={{ base: "100%", lg: "40%" }}
            //             mt={4}
            //             mb={{ base: "32px", lg: 0 }}
            //         >
            //             <Collapsable
            //                 isCollapsed={isNoticeCollapsed}
            //                 iconColor=""
            //                 onToggleCollapse={() =>
            //                     setIsNoticeCollapsed(!isNoticeCollapsed)
            //                 }
            //                 title={
            //                     <Text>Looking to enrol multiple students?</Text>
            //                 }
            //             >
            //                 <Text fontSize={14} color="gray.600">
            //                     Begin by enrolling your first student and you'll
            //                     have the option to enrol additional students
            //                     once you've completed the payment step and
            //                     logged into your Parent Portal
            //                 </Text>
            //             </Collapsable>
            //         </Box>
            //     )
            // }
        >
            <SimpleGrid
                id="main_grid"
                columns={{
                    base: 1,
                    xl:
                        productFamilies !== null && productFamilies.length === 1
                            ? 1
                            : productFamilies !== null &&
                              productFamilies.length === 2
                            ? 2
                            : productFamilies !== null &&
                              productFamilies.length === 3
                            ? 3
                            : 4
                }}
                gridGap={{
                    xl: "32px"
                }}
                mx={{
                    base: undefined,
                    md: undefined,
                    xl:
                        productFamilies !== null && productFamilies.length < 3
                            ? "auto"
                            : undefined
                }}
            >
                {productFamilies !== null &&
                    productFamilies.map(pF => {
                        return (
                            <Fragment key={pF.id}>
                                <Card<ProductFamilyInterface>
                                    classHierarchy={[
                                        ClassesEnum.AgeGroupContainer,
                                        ClassesEnum.Card
                                    ]}
                                    action={`selectAgeAndProudctFamily-${pF.id}`}
                                    item={pF}
                                    key={pF.id}
                                    width={{ xl: "290px", hd: "296px" }}
                                    height="auto"
                                    isClickable={false}
                                    cardBorder={{ base: "0px", xl: "2px" }}
                                    boxShadow={{
                                        base: "0px",
                                        xl: "0px 9px 18px -3px rgba(111,112,112,0.18)"
                                    }}
                                    _last={{
                                        base: {
                                            ".chakra-wrap__list": {
                                                justifyContent: " "
                                            },
                                            ".chakra-wrap__listitem": {
                                                width: " "
                                            }
                                        },
                                        xl: {
                                            ".chakra-wrap__list": {
                                                justifyContent: "stretch"
                                            },
                                            ".chakra-wrap__listitem": {
                                                width: "100%"
                                            }
                                        }
                                    }}
                                >
                                    <Flex
                                        direction="column"
                                        h="full"
                                        w="full"
                                        gap={3}
                                        align={{ base: "start", xl: "center" }}
                                    >
                                        <Flex
                                            id="product_family_name_container"
                                            direction="column"
                                            alignItems={{
                                                base: "start",
                                                xl: "center"
                                            }}
                                            w="full"
                                            borderBottom={{
                                                base: "0",
                                                xl: "1px solid var(--euka-colors-gray-200)"
                                            }}
                                            pb={3}
                                            gap={2}
                                            h="fit-content"
                                        >
                                            <Text
                                                fontSize={14}
                                                textAlign="center"
                                                type="subHeading"
                                                data-tracking-id={getTrackingId(
                                                    [
                                                        ClassesEnum.AgeGroupContainer,
                                                        ClassesEnum.Card,
                                                        ClassesEnum.Text
                                                    ],
                                                    `productFamilyName-${pF.id}`
                                                )}
                                            >
                                                {pF.name}
                                            </Text>
                                            <Image
                                                w={[100, null, "auto"]}
                                                h={[20, null, 89]}
                                                objectFit="contain"
                                                src={pF.coverImage}
                                                alt={pF.name}
                                                pointerEvents="none"
                                                userSelect="none"
                                                display={{
                                                    base: "none",
                                                    xl: "block"
                                                }}
                                            />
                                        </Flex>
                                        <Flex
                                            id="grades_container"
                                            flexWrap="wrap"
                                            gap={4}
                                            alignItems={{
                                                base: "center",
                                                xl: "center"
                                            }}
                                            mt={{ base: 4, xl: 0 }}
                                            justifyContent="center"
                                        >
                                            <Wrap
                                                justify={{
                                                    base: "start",
                                                    xl: "center"
                                                }}
                                            >
                                                {pF.grades.length < 1 && (
                                                    <Text
                                                        fontSize={14}
                                                        color="gray.600"
                                                    >
                                                        No grades found
                                                    </Text>
                                                )}
                                                {sortByProperty(
                                                    pF.grades,
                                                    "id"
                                                ).map(g => (
                                                    <WrapItem
                                                        key={g.id}
                                                        style={{
                                                            justifyContent:
                                                                "center"
                                                        }}
                                                    >
                                                        <Box flex="10%">
                                                            <SelectablePill
                                                                isActive={
                                                                    checkout
                                                                        .grade
                                                                        ?.name ===
                                                                    g.name
                                                                }
                                                                onClick={() =>
                                                                    onSelectProductFamily(
                                                                        g,
                                                                        pF
                                                                    )
                                                                }
                                                                classHierarchy={[
                                                                    ClassesEnum.AgeGroupContainer,
                                                                    ClassesEnum.Card,
                                                                    ClassesEnum.Button
                                                                ]}
                                                                action={`selectAge-${g.shortName}`}
                                                            >
                                                                {g.name}
                                                            </SelectablePill>
                                                        </Box>
                                                    </WrapItem>
                                                ))}
                                            </Wrap>
                                        </Flex>
                                    </Flex>
                                </Card>
                                <Divider
                                    display={{ base: "block", xl: "none" }}
                                />
                            </Fragment>
                        );
                    })}
                {isProductFamiliesLoading && <Skeleton />}
            </SimpleGrid>
        </ContentLayout>
    );
};

export default AgeGroupContainer;
